import { useMemo } from "react"
import { Chart } from "react-chartjs-2"
import { DataTable, EmptyState, ErrorState, MoneyText, StatusPill } from "procurement/design"
import { useGetBudgetDashboardSpendingByCenterQuery } from "shared/redux/endpoints/procurementV2"
import { CenterPicker, useCenterPicker } from "./CenterPicker"
import { SERIES, baseOptions, gridColor, moneyTick, pctOf, pctTick, tickColor } from "./chartSetup"

const money = (key) => (r) => <MoneyText value={r[key]} />

const COLUMNS = [
  { key: "budgetCenterName", header: "Center", sortable: true, className: "pc-col-title" },
  { key: "categoryCount", header: "Categories", sortable: true, align: "right" },
  { key: "totalBudget", header: "Total budget", sortable: true, align: "right", render: money("totalBudget") },
  { key: "committed", header: "Committed", sortable: true, align: "right", render: money("committed") },
  { key: "unpaid", header: "Unpaid", sortable: true, align: "right", render: money("unpaid") },
  { key: "paid", header: "Paid", sortable: true, align: "right", render: money("paid") },
  { key: "usedAmount", header: "Used", sortable: true, align: "right", render: money("usedAmount") },
  { key: "remaining", header: "Remaining", sortable: true, align: "right", render: money("remaining") },
  {
    key: "usedPercentage",
    header: "Used %",
    sortable: true,
    align: "right",
    render: (r) => (r.isOverBudget ? <StatusPill tone="danger" label={pctOf(r.usedPercentage)} /> : pctOf(r.usedPercentage)),
  },
]

const CenterSpendingChart = ({ params }) => {
  const { data, isFetching, isError } = useGetBudgetDashboardSpendingByCenterQuery({ ...params, size: 500 })
  const allRows = useMemo(
    () => [...(data?.items || [])].sort((a, b) => (b.usedPercentage ?? 0) - (a.usedPercentage ?? 0)),
    [data]
  )
  const { filtered: rows, pickerProps } = useCenterPicker(allRows)

  const chart = useMemo(
    () => ({
      labels: rows.map((r) => r.budgetCenterName),
      datasets: [
        {
          type: "line",
          label: "Used %",
          data: rows.map((r) => r.usedPercentage),
          yAxisID: "pct",
          borderColor: SERIES.danger,
          backgroundColor: SERIES.danger,
          pointRadius: 3,
          tension: 0.25,
        },
        { type: "bar", label: "Total budget", data: rows.map((r) => r.totalBudget), backgroundColor: SERIES.budget, yAxisID: "money" },
        { type: "bar", label: "Used", data: rows.map((r) => r.usedAmount), backgroundColor: SERIES.used, yAxisID: "money" },
      ],
    }),
    [rows]
  )

  const options = useMemo(
    () => ({
      ...baseOptions,
      scales: {
        x: { grid: { display: false }, ticks: { color: tickColor, autoSkip: false, maxRotation: 60 } },
        money: { position: "left", ticks: { callback: moneyTick, color: tickColor }, grid: { color: gridColor } },
        pct: { position: "right", ticks: { callback: pctTick, color: tickColor }, grid: { display: false }, beginAtZero: true },
      },
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          callbacks: {
            label: (ctx) =>
              ctx.dataset.yAxisID === "pct"
                ? `Used: ${pctOf(ctx.raw)}`
                : baseOptions.plugins.tooltip.callbacks.label(ctx),
          },
        },
      },
    }),
    []
  )

  if (isError) return <ErrorState text="Couldn't load spending by center." />
  if (!isFetching && allRows.length === 0) return <EmptyState title="No center spending for this period." />

  return (
    <>
      <CenterPicker {...pickerProps} />
      <div className="br-chart-scroll">
        <div className="br-chart" style={{ height: 340, minWidth: Math.max(600, rows.length * 44) }}>
          <Chart type="bar" data={chart} options={options} />
        </div>
      </div>
      <DataTable
        columns={COLUMNS}
        rows={rows}
        isLoading={isFetching}
        rowKey={(r) => r.budgetCenterId}
        density="compact"
        defaultPageSize={10}
      />
    </>
  )
}

export default CenterSpendingChart
